// "That home on the way": a rainy dusk walk past lit homes → the white house → "(one day)" → held under an orange umbrella.
export const homeOnTheWay = {
  slug: "that-home-on-the-way",
  duration: 29,
  stages: [
    { id: "street", at: 0, hint: "walking home in the rain" },
    { id: "house", at: 8.5, hint: "the white house" },
    { id: "wish", at: 14, hint: "tap the window" },
    { id: "held", at: 20, hint: "held" },
  ],
  line: { text: "Even through the rain of life, your safety becomes your home.", from: 21, to: 28.5, pos: "low" },
  words: { list: ["(one day)"], from: 14.5, to: 20, still: true },
  touch: { kind: "window", from: 14, until: 20.5 },
  // painted panorama: a rainy dusk street, the white house, the orange umbrella
  layers: [
    { src: "/arcs/homeway/rain.webp", wrap: 3.48, th: 0, h: 8.4, y: 1.8, r: 4.9, water: 0.36, opacity: [[0, 1], [14, 0.78], [20.5, 0.78], [22, 1]], show: [-1, 0, 99, 99] },
  ],
  umbrella: { open: [20, 22.5], color: "#E8762B" },
  ambient: { scene: "rainwalk", until: 99, then: [[12, "rain"]] },
  look: { start: 0.02, lift: [20, 22.5], end: 0.32, yaw: [[0, -0.5], [6, 0.35], [11, 0], [29, 0]] },
  calm: { duration: 12, fade: [4, 8.5], line: [5.5, 11.5], backdrop: "home", pos: "low" },
  speed: [[0, 1], [8, 1], [12, 0], [29, 0]],
  frag: `
uniform sampler2D uMap;
uniform vec4 uCrop;
uniform float uT, uPhase, uAspect, uTileH, uRadius, uCy, uSurface, uWarm;
varying vec3 vPos;
${"__NOISE__"}
float hash(float n){ return fract(sin(n * 127.1) * 43758.5453); }
float hash2(vec2 p){ return fract(sin(dot(p, vec2(41.3, 289.1))) * 43758.5453); }
vec3 tap(vec2 uv){
  vec2 m = 1.0 - abs(1.0 - mod(uv, 2.0));
  return texture2D(uMap, mix(uCrop.xy, uCrop.zw, m)).rgb;
}
const float P = 0.95;
// the street: houses along the way; house 0 (dead ahead once the walk ends) is the white house
vec4 street(float th, float y, out float glow){
  float walk = 9.0 - uPhase * 0.9;
  float s = th + walk * P;
  float id = floor(s / P + 0.5);
  float u = (s - id * P) / P;
  bool white = abs(id) < 0.5;
  float hw = white ? 0.34 : 0.3 + 0.06 * hash(id);
  float hh = white ? 3.3 : 2.6 + 0.9 * hash(id + 4.0);
  float roof = hh + (hw - abs(u)) * 2.4;
  float body = step(abs(u), hw) * step(y, roof);
  vec3 wall = white ? vec3(0.9, 0.89, 0.84) : mix(vec3(0.24, 0.24, 0.32), vec3(0.34, 0.3, 0.36), hash(id + 7.0));
  vec3 c = wall * (0.55 + 0.45 * smoothstep(0.0, 3.0, y));
  glow = 0.0;
  // windows
  vec2 wq = vec2(u / hw, y);
  float on = white ? 1.0 : step(0.3, hash(id + 13.0));
  float win = white
    ? step(abs(wq.x - 0.38), 0.22) * step(abs(y - 1.9), 0.42)
    : step(abs(abs(wq.x) - 0.45), 0.16) * step(abs(y - 1.95), 0.3);
  vec3 lamp = mix(vec3(1.0, 0.78, 0.36), vec3(1.0, 0.86, 0.5), white ? uWarm : 0.0);
  float wk = white ? 1.0 + 0.7 * uWarm : 0.85;
  c = mix(c, lamp * wk, win * on);
  glow = win * on * (white ? 1.0 + uWarm : 0.5);
  if (white) {
    float door = step(abs(wq.x + 0.42), 0.16) * step(y, 1.55);
    c = mix(c, vec3(0.72, 0.13, 0.12), door);
  }
  return vec4(c, body);
}
void main(){
  float held = smoothstep(20.5, 23.0, uT);
  float melt = smoothstep(21.5, 26.5, uT);
  float n = snoise(vPos * 0.5 + vec3(0.0, 0.0, uT * 0.03)) * 0.5 + 0.5;
  float th = atan(vPos.x, -vPos.z);
  vec3 col;
  vec2 local;
  vec3 dusk = vec3(0.36, 0.33, 0.45);
  if (uSurface < 0.5) {
    float y = vPos.y;
    vec3 sky = mix(vec3(0.44, 0.4, 0.52), vec3(0.24, 0.22, 0.32), smoothstep(3.0, 9.0, y)) + 0.04 * n;
    float g;
    vec4 h = street(th, y, g);
    col = mix(sky, h.rgb, h.a);
    col += vec3(1.0, 0.75, 0.4) * g * 0.08;
    local = vec2(th * uRadius, y - uCy);
  } else if (uSurface < 1.5) {
    vec2 xz = vPos.xz;
    float r = length(xz);
    float fth = atan(xz.x, -xz.y);
    float g;
    vec4 h = street(fth + 0.004 * sin(r * 9.0 + uT * 2.0), 1.95, g);
    vec3 road = vec3(0.1, 0.1, 0.13) + 0.03 * n;
    float wet = smoothstep(0.4, 0.62, snoise(vec3(xz * 0.4, 2.0)) * 0.5 + 0.5);
    col = road + vec3(1.0, 0.75, 0.38) * g * smoothstep(0.8, uRadius, r) * mix(0.15, 0.6, wet);
    // warm light spilling from the white house onto the wet road
    float spill = exp(-pow(fth * 3.0, 2.0)) * smoothstep(uRadius * 0.3, uRadius, r) * smoothstep(8.0, 12.0, uT);
    col += vec3(1.0, 0.7, 0.35) * spill * (0.22 + 0.2 * uWarm);
    local = xz;
  } else {
    col = vec3(0.22, 0.2, 0.3);
    local = vPos.xz;
  }
  // rain: thin falling streaks everywhere, it never stops
  vec2 rc = vec2((uSurface < 0.5 ? th * uRadius : vPos.x) * 9.0, (uSurface < 0.5 ? vPos.y : vPos.z) * 1.6 + uT * 11.0);
  vec2 cell = floor(rc);
  float hr = hash2(cell);
  vec2 f = fract(rc);
  float streak = step(0.6, hr) * (1.0 - smoothstep(0.0, 0.05, abs(f.x - hr))) * smoothstep(0.0, 0.3, f.y) * (1.0 - smoothstep(0.55, 0.75, f.y));
  col += vec3(0.75, 0.8, 0.95) * streak * (uSurface < 1.5 ? 0.22 : 0.12);
  vec2 tile = vec2(uTileH * uAspect, uTileH);
  vec3 paint = tap(local / tile + 0.5);
  float drip = uSurface < 0.5 ? (10.0 - vPos.y) / 10.0 : 0.5 + length(vPos.xz) / (uRadius * 3.0);
  float m = smoothstep(0.0, 0.25, melt * 1.3 - drip * 0.6 - n * 0.25);
  col = mix(col, paint + vec3(0.75, 0.8, 0.95) * streak * 0.15, m);
  col = mix(col, col * vec3(1.05, 0.98, 0.92), held * 0.4);
  gl_FragColor = vec4(clamp(col, 0.0, 1.0), 1.0);
  #include <colorspace_fragment>
}`,
};
